<script setup lang="ts">
import type { IActivity, IEvent } from '@/types'

const { moneyFormater } = useMoneyFormater()
const { propStateInQuery } = useQueryProp()

const props = defineProps<{
  events: IEvent<any>[]
  activity: IActivity
}>()

const { events, activity } = toRefs(props)

const { formatDate, getDate, getDayOfWeek, getMonth } = useDate()

const colors = [
  { label: 'Blue 300', code: '165db7' },
  { label: 'Blue 200', code: '2070bf' },
  { label: 'Blue 100', code: '2983c7' },
  { label: 'Green 300', code: '288f6f' },
  { label: 'Green 200', code: '279b17' },
  { label: 'Green 100', code: '89a81d' },
  { label: 'Yellow 200', code: 'f09a1a' },
  { label: 'Yellow 100', code: 'EAB522' },
  { label: 'Orange', code: 'f57e11' },
  { label: 'Red 200', code: 'd82422' },
  { label: 'Red 100', code: 'e7511a' },
  { label: 'Violet 300', code: '77185e' },
  { label: 'Violet 200', code: '7915ad' },
  { label: 'Violet 100', code: 'a91d68' },
  { label: 'Brown', code: '741a0e' }
]

function getRandomColor() {
  return colors[Math.floor(Math.random() * (colors.length - 1))].code
}

const isMounted = ref(false)

const selectedDate = ref<Date | null>(null)

function selectStartDate(value: string) {
  selectedDate.value = new Date(Date.parse(value))
}

const activeDates = computed(() => {
  const dates = [...events.value.map((el) => el.scheduleInfo.presentationDate)]
  return [...new Set(dates).values()]
})

const groupByPresentationDate = computed(() => {
  return events.value.reduce((prev, next) => {
    if (prev[next.scheduleInfo.presentationDate])
      prev[next.scheduleInfo.presentationDate] = [...prev[next.scheduleInfo.presentationDate], next]
    else prev[next.scheduleInfo.presentationDate] = [next]

    console.log(prev)

    return prev
  }, {} as Record<string, IEvent<any>[]>)
})

console.log(groupByPresentationDate)

selectStartDate(activeDates.value[0])

const groupByDate = computed(() => {
  const groupedEvents = events.value.reduce((acc, event) => {
    const date = event.scheduleInfo.presentationDate

    if (!acc[date]) {
      acc[date] = {
        presentationDate: event.scheduleInfo.presentationDate,
        sessionDate: event.scheduleInfo.sessionDate,
        events: []
      }
    }

    acc[date].events.push(event)
    return acc
  }, {} as Record<string, { presentationDate: string; sessionDate: string; events: IEvent<any>[] }>)

  return Object.values(groupedEvents)
})

function isWeekend(day) {
  return new Date(day).getDay() === 0 || new Date(day).getDay() === 6
}

function getWeekName(day: string) {
  const dayOfWeek = new Date(day).getDay()
  const dict: { [key: string]: any } = {
    0: 'ВС',
    1: 'ПН',
    2: 'ВТ',
    3: 'СР',
    4: 'ЧТ',
    5: 'ПТ',
    6: 'СБ'
  }
  return dict[dayOfWeek]
}

const minPriceEvent = computed(() => {
  return events.value.reduce((min, event) => {
    return event.minPrice < min.minPrice ? event : min
  }, events.value[0])
})

const length = ref(10)

const showEvents = computed(() => events.value.slice(0, length.value))

function showMore() {
  if (length.value > showEvents.value.length) {
    return
  }
  length.value = length.value + 10
}

function scrollToTop() {
  const firstEvent = document.querySelector('.mobileEvents')
  if (firstEvent) {
    firstEvent.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}
</script>

<template>
  <main class="relative mx-auto h-full w-full flex flex-col px-4 lg:px-17 py-10">
    <div class="flex mb-8 flex-col-reverse lg:flex-row gap-6 w-full justify-between">
      <div class="lg:max-w-1/2 flex flex-col gap-2">
        <h6 class="text-base font-500 font-sf-pro text-gray-500 flex flex-row justify-between">
          <span class="text-right lg:text-left text-base font-500 font-sf-pro text-gray-500">
            {{ activity.organization.name }}
          </span>
        </h6>
        <h4 class="text-justify lg:text-left text-4xl font-500 font-sf-pro text-gray-900">
          {{ activity.title }}
        </h4>
      </div>
    </div>

    <p class="font-sf-pro text-xl font-medium pb-4">Расписание</p>
    <hr class="bg-gray-500 mb-0" />

    <div
      v-if="events.length > 0"
      class="flex flex-col pr-4 mb-10 overflow-y-auto min-w-full w-full"
    >
      <div class="hidden md:flex flex-col">
        <table>
          <tr v-for="(date, ind) in groupByDate" class="border-b last:border-none border-gray-200">
            <td style="vertical-align: top" class="py-8">
              <div class="flex flex-row space-x-3 items-center">
                <p class="text-[48px] leading-10">
                  {{ Number(date.presentationDate.split('-')[2]) }}
                </p>
                <div class="flex flex-col pt-2 space-y-1 font-sf-pro justify-center">
                  <p
                    class="text-sm"
                    :class="
                      isWeekend(date.sessionDate) ? 'text-decorative-scarlet' : 'text-gray-500'
                    "
                  >
                    {{ getDayOfWeek(date.sessionDate).toLowerCase() }}
                  </p>
                  <p class="text-sm">
                    {{ formatDate(date.sessionDate, 'D MMMM').split(' ')[1] }}
                  </p>
                </div>
              </div>
            </td>
            <td style="vertical-align: top" class="py-8">
              <div class="grid grid-cols-1 gap-4">
                <div
                  v-for="event in date.events"
                  class="flex flex-row font-sf-pro space-x-3 justify-between items-center"
                >
                  <p class="text-base">
                    {{ formatDate(event.scheduleInfo.sessionDate, 'HH:mm') }}
                  </p>
                  <p class="text-[#7f7f7f]">{{ event.place.name }}</p>
                  <nuxt-link
                    class="w-fit"
                    :to="
                      event.scheduleInfo.saleStatus === 'closed'
                        ? ''
                        : propStateInQuery(`/${event.id}/fullscreen`)
                    "
                  >
                    <t-button
                      rounded="large"
                      class="font-normal !px-4 w-full !text-sm h-9"
                      :class="
                        event.scheduleInfo.saleStatus === 'closed'
                          ? 'opacity-60 cursor-not-allowed'
                          : ''
                      "
                    >
                      Билеты от {{ moneyFormater(event.minPrice) }}
                    </t-button>
                  </nuxt-link>
                </div>
              </div>
            </td>
          </tr>
        </table>
      </div>
      <!-- mobile -->
      <div class="mobileEvents flex md:hidden flex-col">
        <div
          class="flex flex-row py-5 border-b last:border-none border-gray-200 space-x-5"
          v-for="event in showEvents"
          :key="event.id"
        >
          <div class="flex pt-0.5 font-sf-pro items-center font-medium flex-col space-y-1">
            <p class="text-[32px] !leading-6">
              {{ Number(event.scheduleInfo.presentationDate.split('-')[2]) }}
            </p>
            <p class="text-sm">
              {{ formatDate(event.scheduleInfo.presentationDate, 'MMM').split('.')[0] }}
            </p>
          </div>

          <div class="grid grid-cols-1 gap-2">
            <p class="text-sm font-semibold">
              {{ getWeekName(event.scheduleInfo.presentationDate) }}
              {{ formatDate(event.scheduleInfo.sessionDate, 'HH:mm') }}
            </p>

            <p class="text-sm text-[#7f7f7f] font-semibold">{{ event.place.name }}</p>

            <nuxt-link
              class="w-fit mt-3"
              :to="
                event.scheduleInfo.saleStatus === 'closed'
                  ? ''
                  : propStateInQuery(`/${event.id}/fullscreen`)
              "
            >
              <t-button
                rounded="large"
                class="font-normal !px-4 w-full !text-sm h-8"
                :class="
                  event.scheduleInfo.saleStatus === 'closed' ? 'opacity-60 cursor-not-allowed' : ''
                "
              >
                Билеты от {{ moneyFormater(event.minPrice) }}
              </t-button>
            </nuxt-link>
          </div>
        </div>
        <div
          v-if="events.length > 10 && length < events.length"
          class="w-fit mt-6 mb-6 md:hidden mx-auto font-sf-pro text-base rounded-full w-full px-4 py-3 bg-[#00000012] cursor-pointer text-center"
          @click="showMore()"
        >
          Показать еще
        </div>
      </div>
    </div>
  </main>
  <!-- <div
    class="absolute md:hidden bottom-0 h-20 border-t botder-gray-200 w-full px-4 bg-white flex justify-center items-center"
    style="box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.05)"
  >
    <t-button
      rounded="large"
      class="font-normal w-full !px-4 w-full !text-base h-11"
      @click="scrollToTop()"
    >
      Билеты от {{ moneyFormater(minPriceEvent.minPrice) }}
    </t-button>
  </div> -->
</template>
